import { useState, type FormEvent, type ReactNode } from 'react';
import {
  ArrowRight,
  BarChart3,
  Boxes,
  Check,
  CheckCircle2,
  ChevronRight,
  Cloud,
  Code2,
  Database,
  FileCheck2,
  Gauge,
  Headphones,
  Layers3,
  ExternalLink,
  LockKeyhole,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Play,
  Send,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Workflow,
  X,
  Zap,
} from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';

const whatsappBase = 'https://wa.me/5521969858888';
const whatsappUrl = `${whatsappBase}?text=${encodeURIComponent(
  'Olá, gostaria de conhecer as soluções da First Flow.',
)}`;

const products = [
  {
    icon: LockKeyhole,
    eyebrow: 'Segurança',
    title: 'Cofre de senhas',
    description:
      'Credenciais organizadas, acesso controlado e mais segurança para equipes que não podem depender de planilhas.',
    features: ['Acesso por perfil', 'Compartilhamento seguro', 'Histórico e auditoria'],
    accent: '#0778aa',
  },
  {
    icon: Headphones,
    eyebrow: 'Atendimento',
    title: 'Central de serviços',
    description:
      'Chamados, SLAs e comunicação reunidos em um fluxo simples para quem solicita e para quem resolve.',
    features: ['Portal de atendimento', 'Filas e automações', 'Indicadores de SLA'],
    accent: '#2f91ba',
  },
  {
    icon: FileCheck2,
    eyebrow: 'Governança',
    title: 'Gestão documental',
    description:
      'Documentos, revisões, responsáveis e evidências sob controle — com rastreabilidade do início ao fim.',
    features: ['Ciclo de aprovação', 'Controle de versões', 'Trilha de auditoria'],
    accent: '#344d56',
  },
  {
    icon: BarChart3,
    eyebrow: 'Inteligência',
    title: 'Dashboards & dados',
    description:
      'Indicadores vivos para transformar dados espalhados em decisões rápidas, visíveis e confiáveis.',
    features: ['Integração de fontes', 'Painéis gerenciais', 'Alertas inteligentes'],
    accent: '#8db7c8',
  },
];

const services = [
  {
    number: '01',
    icon: Workflow,
    title: 'Automação de processos',
    text: 'Mapeamos tarefas repetitivas e criamos fluxos que trabalham com sua equipe — não contra ela.',
  },
  {
    number: '02',
    icon: Code2,
    title: 'Software sob medida',
    text: 'Aplicações web e mobile desenhadas para o seu cenário, integradas aos sistemas que já existem.',
  },
  {
    number: '03',
    icon: Cloud,
    title: 'Cloud & integrações',
    text: 'Arquitetura segura e escalável na Microsoft Azure, com portabilidade e sem amarras desnecessárias.',
  },
  {
    number: '04',
    icon: Gauge,
    title: 'Dados & performance',
    text: 'Painéis, APIs e observabilidade para transformar operação em informação acionável.',
  },
];

const faqs = [
  {
    question: 'Os sistemas são prontos ou feitos sob medida?',
    answer:
      'Trabalhamos nos dois formatos. Você pode contratar uma solução já validada e personalizar a implantação, ou iniciar um projeto totalmente sob medida quando o processo exige regras específicas.',
  },
  {
    question: 'Como funciona a contratação?',
    answer:
      'Começamos com uma conversa de diagnóstico e uma demonstração. Depois definimos escopo, implantação, suporte e o modelo comercial mais adequado — assinatura, licença ou projeto fechado.',
  },
  {
    question: 'Vocês migram dados de outros sistemas?',
    answer:
      'Sim. A migração é planejada conforme a origem, volume e qualidade dos dados. Antes de qualquer mudança definitiva, validamos amostras, regras e uma estratégia segura de reversão.',
  },
  {
    question: 'Onde as soluções da First Flow são hospedadas?',
    answer:
      'Nossa arquitetura principal utiliza a Microsoft Azure, com serviços gerenciados, monitoramento, segurança e possibilidade de portabilidade por meio de contêineres e PostgreSQL.',
  },
  {
    question: 'Há suporte depois da implantação?',
    answer:
      'Sim. A First Flow acompanha a entrada em operação e pode oferecer suporte contínuo, evolução funcional, monitoramento e acordos de nível de serviço conforme a criticidade.',
  },
];

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.18em] text-[#69c1e3]">
      <span className="h-px w-7 bg-[#0778aa]" />
      {children}
    </div>
  );
}

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [interest, setInterest] = useState('Conhecer as soluções');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');

  const selectProduct = (product: string) => {
    setInterest(product);
    document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendLead = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const message = [
      'Olá, gostaria de falar com a First Flow.',
      name ? `Nome: ${name}` : '',
      company ? `Empresa: ${company}` : '',
      `Interesse: ${interest}`,
    ]
      .filter(Boolean)
      .join('\n');
    window.open(`${whatsappBase}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <main className="overflow-hidden bg-[#20343c] text-white">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/[0.08] bg-[#20343c]/88 backdrop-blur-xl">
        <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-6 lg:px-10">
          <a href="#inicio" aria-label="First Flow — início" className="flex items-center">
            <img src="/firstflow-logo-light.png" alt="First Flow" className="h-11 w-auto" />
          </a>

          <nav aria-label="Navegação principal" className="hidden items-center gap-8 text-sm text-white/65 lg:flex">
            {[
              ['Soluções', '#solucoes'],
              ['Produtos', '#produtos'],
              ['Como funciona', '#processo'],
              ['A First Flow', '#sobre'],
              ['FAQ', '#faq'],
            ].map(([label, href]) => (
              <a key={label} href={href} className="transition hover:text-white">
                {label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="group hidden h-11 items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-5 text-sm font-semibold transition hover:border-[#69c1e3]/60 hover:bg-[#0778aa] hover:text-white sm:inline-flex"
            >
              Falar com especialista
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <Button
              type="button"
              variant="outline"
              size="icon-lg"
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((current) => !current)}
              className="border-white/10 bg-white/5 text-white hover:bg-white/10 lg:hidden"
            >
              {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </Button>
          </div>
        </div>

        {menuOpen && (
          <nav aria-label="Navegação móvel" className="border-t border-white/8 bg-[#20343c] px-6 py-6 lg:hidden">
            <div className="mx-auto flex max-w-[1440px] flex-col gap-1">
              {[
                ['Soluções', '#solucoes'],
                ['Produtos', '#produtos'],
                ['Como funciona', '#processo'],
                ['A First Flow', '#sobre'],
                ['FAQ', '#faq'],
                ['Contato', '#contato'],
              ].map(([label, href]) => (
                <a
                  key={label}
                  href={href}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between rounded-xl px-3 py-3 text-white/75 transition hover:bg-white/5 hover:text-white"
                >
                  {label}
                  <ChevronRight className="size-4" />
                </a>
              ))}
            </div>
          </nav>
        )}
      </header>

      <section id="inicio" className="relative mx-auto grid min-h-screen w-full max-w-[1440px] items-center gap-14 px-6 pb-20 pt-32 lg:grid-cols-[1.03fr_.97fr] lg:px-10 lg:pb-24 lg:pt-28">
        <div className="pointer-events-none absolute left-[12%] top-[-18rem] size-[44rem] rounded-full bg-[#0778aa]/20 blur-[120px]" />
        <div className="pointer-events-none absolute bottom-[-20rem] right-[-10rem] size-[45rem] rounded-full bg-[#69c1e3]/10 blur-[140px]" />

        <div className="relative z-10 max-w-3xl">
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#69c1e3]/35 bg-[#0778aa]/15 px-4 py-2 text-xs font-semibold uppercase tracking-[.15em] text-[#9edcf2]">
            <Sparkles className="size-3.5" />
            Tecnologia que vira resultado
          </div>

          <h1 className="max-w-[820px] text-[clamp(3.4rem,7.2vw,7.1rem)] font-semibold leading-[.92] tracking-[-.065em]">
            Software que faz sua empresa{' '}
            <span className="bg-gradient-to-r from-[#69c1e3] via-[#24a6d7] to-[#0778aa] bg-clip-text text-transparent">
              fluir.
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#cbd8dd] sm:text-xl">
            Sistemas prontos e soluções sob medida para automatizar processos, proteger dados e acelerar o crescimento do seu negócio.
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a href="#produtos" className="group inline-flex h-14 items-center justify-center gap-3 rounded-full bg-[#0778aa] px-7 font-bold text-white transition hover:bg-[#0a8fc8]">
              Conhecer os sistemas
              <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#processo" className="inline-flex h-14 items-center justify-center gap-3 rounded-full border border-white/15 bg-white/[0.04] px-7 font-semibold text-white transition hover:bg-white/[0.09]">
              <span className="grid size-7 place-items-center rounded-full bg-white/10"><Play className="ml-0.5 size-3.5 fill-white" /></span>
              Como trabalhamos
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-white/55">
            <span className="flex items-center gap-2"><CheckCircle2 className="size-4 text-[#69c1e3]" /> Implantação acompanhada</span>
            <span className="flex items-center gap-2"><ShieldCheck className="size-4 text-[#69c1e3]" /> Nuvem e segurança</span>
          </div>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-[660px] lg:ml-auto">
          <div className="absolute -inset-10 rounded-full bg-[#0778aa]/15 blur-[80px]" />
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#263f48]/90 p-3 shadow-[0_50px_120px_rgba(12,29,36,.5)] backdrop-blur-xl sm:p-4">
            <div className="rounded-[1.35rem] border border-white/8 bg-[#344d56]">
              <div className="flex h-14 items-center justify-between border-b border-white/8 px-5">
                <div className="flex items-center gap-2"><span className="size-2.5 rounded-full bg-[#ff6b6b]" /><span className="size-2.5 rounded-full bg-[#ffd166]" /><span className="size-2.5 rounded-full bg-[#0778aa]" /></div>
                <span className="rounded-full bg-white/5 px-3 py-1 text-[10px] font-medium text-white/45">FIRST FLOW · CONTROL CENTER</span>
              </div>
              <div className="grid min-h-[430px] grid-cols-[76px_1fr] sm:grid-cols-[92px_1fr]">
                <aside className="border-r border-white/8 p-4">
                  <div className="grid size-10 place-items-center rounded-xl bg-[#0778aa] text-sm font-black text-white">FF</div>
                  <div className="mt-8 space-y-4">{[1, 2, 3, 4].map((item) => <span key={item} className={`block h-9 rounded-xl ${item === 1 ? 'bg-[#0778aa]/30 ring-1 ring-[#69c1e3]/35' : 'bg-white/[0.035]'}`} />)}</div>
                </aside>
                <div className="p-5 sm:p-7">
                  <div className="flex items-start justify-between gap-3">
                    <div><p className="text-xs text-white/40">Visão geral</p><h2 className="mt-1 text-xl font-semibold">Sua operação, clara.</h2></div>
                    <span className="rounded-full bg-[#0778aa]/25 px-3 py-1.5 text-[10px] font-semibold text-[#9edcf2]">Tudo operando</span>
                  </div>
                  <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {[
                      ['Processos ativos', '48', '+12%'],
                      ['Horas economizadas', '164h', '+28%'],
                      ['Disponibilidade', '99,9%', 'Estável'],
                    ].map(([label, value, delta], index) => (
                      <div key={label} className={`${index === 2 ? 'col-span-2 sm:col-span-1' : ''} rounded-2xl border border-white/7 bg-white/[0.035] p-4`}>
                        <p className="text-[10px] text-white/40">{label}</p><p className="mt-3 text-2xl font-semibold">{value}</p><p className="mt-1 text-[10px] text-[#9edcf2]">{delta}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-2xl border border-white/7 bg-white/[0.035] p-5">
                    <div className="flex items-center justify-between"><span className="text-xs font-medium">Performance mensal</span><span className="text-[10px] text-white/35">Últimos 6 meses</span></div>
                    <div className="mt-8 flex h-28 items-end gap-2">{[38, 55, 47, 68, 62, 88, 72, 96, 83, 100, 92, 112].map((height, index) => <span key={index} className="flex-1 rounded-t-sm bg-gradient-to-t from-[#045d83]/45 to-[#69c1e3]" style={{ height: `${height}px`, opacity: 0.45 + index * 0.04 }} />)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-white/[0.06] bg-white/[0.02]">
        <div className="mx-auto grid max-w-[1440px] grid-cols-2 divide-x divide-y divide-white/[0.06] px-6 sm:grid-cols-4 lg:px-10">
          {[
            ['Cloud-first', 'Arquitetura pronta para crescer'],
            ['Security by design', 'Proteção desde a primeira decisão'],
            ['Integração real', 'APIs, dados e sistemas conectados'],
            ['Suporte próximo', 'Tecnologia com gente do outro lado'],
          ].map(([title, text]) => (
            <div key={title} className="px-4 py-7 first:pl-0 sm:px-7">
              <p className="text-sm font-semibold text-white">{title}</p>
              <p className="mt-1 text-xs leading-relaxed text-white/40">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="solucoes" className="relative mx-auto max-w-[1440px] px-6 py-28 lg:px-10 lg:py-36">
        <div className="grid gap-12 lg:grid-cols-[.8fr_1.2fr] lg:gap-20">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <SectionLabel>Soluções</SectionLabel>
            <h2 className="max-w-lg text-4xl font-semibold leading-tight tracking-[-.045em] sm:text-5xl lg:text-6xl">
              Do problema à operação. <span className="text-white/35">Sem perder o fluxo.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/50">
              Estratégia, design e engenharia no mesmo time para entregar sistemas úteis, seguros e prontos para o mundo real.
            </p>
            <a href="#contato" className="group mt-8 inline-flex items-center gap-2 font-semibold text-[#69c1e3]">
              Conte o que precisa resolver
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          <div className="grid gap-px overflow-hidden rounded-[2rem] border border-white/[0.07] bg-white/[0.07] sm:grid-cols-2">
            {services.map(({ number, icon: Icon, title, text }) => (
              <article key={title} className="group min-h-[320px] bg-[#263f48] p-7 transition hover:bg-[#2a424b] sm:p-9">
                <div className="flex items-start justify-between">
                  <span className="grid size-12 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-[#69c1e3] transition group-hover:border-[#69c1e3]/35 group-hover:bg-[#0778aa]/20">
                    <Icon className="size-5" />
                  </span>
                  <span className="font-mono text-xs text-white/25">{number}</span>
                </div>
                <h3 className="mt-14 text-2xl font-semibold tracking-tight">{title}</h3>
                <p className="mt-4 leading-relaxed text-white/45">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="produtos" className="border-y border-white/[0.06] bg-[#f4f7f8] text-[#344d56]">
        <div className="mx-auto max-w-[1440px] px-6 py-28 lg:px-10 lg:py-36">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.18em] text-[#0778aa]"><span className="h-px w-7 bg-[#0778aa]" />Sistemas First Flow</div>
              <h2 className="max-w-3xl text-4xl font-semibold leading-tight tracking-[-.05em] sm:text-5xl lg:text-6xl">
                Tecnologia pronta para entrar em ação.
              </h2>
            </div>
            <p className="max-w-md text-base leading-relaxed text-[#60747c]">
              Soluções modulares, personalizáveis e acompanhadas por quem desenvolve. Escolha o ponto de partida e evolua no seu ritmo.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {products.map(({ icon: Icon, eyebrow, title, description, features, accent }) => (
              <article key={title} className="group flex min-h-[500px] flex-col overflow-hidden rounded-[1.6rem] border border-[#344d56]/12 bg-white p-6 shadow-[0_18px_50px_rgba(52,77,86,.07)] transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_70px_rgba(52,77,86,.14)] sm:p-7">
                <div className="flex items-center justify-between">
                  <span className="grid size-13 place-items-center rounded-2xl" style={{ backgroundColor: `${accent}20`, color: '#344d56' }}><Icon className="size-6" /></span>
                  <span className="rounded-full border border-[#344d56]/12 px-3 py-1 text-[10px] font-bold uppercase tracking-[.12em] text-[#60747c]">Pronto para usar</span>
                </div>
                <p className="mt-10 text-xs font-bold uppercase tracking-[.15em] text-[#0778aa]">{eyebrow}</p>
                <h3 className="mt-3 text-3xl font-semibold tracking-[-.04em]">{title}</h3>
                <p className="mt-4 leading-relaxed text-[#6a7f87]">{description}</p>
                <ul className="mt-7 space-y-3 text-sm text-[#344d56]">
                  {features.map((feature) => <li key={feature} className="flex items-center gap-2.5"><span className="grid size-5 place-items-center rounded-full bg-[#0778aa] text-white"><Check className="size-3" /></span>{feature}</li>)}
                </ul>
                <button
                  type="button"
                  onClick={() => selectProduct(title)}
                  className="group/button mt-auto flex w-full items-center justify-between border-t border-[#344d56]/12 pt-6 text-left text-sm font-bold"
                >
                  Solicitar demonstração
                  <span className="grid size-9 place-items-center rounded-full bg-[#344d56] text-white transition group-hover/button:bg-[#0778aa]"><ArrowRight className="size-4 transition-transform group-hover/button:translate-x-0.5" /></span>
                </button>
              </article>
            ))}
          </div>

          <div className="mt-6 grid items-center gap-7 rounded-[1.6rem] bg-[#344d56] p-7 text-white md:grid-cols-[auto_1fr_auto] sm:p-9">
            <span className="grid size-14 place-items-center rounded-2xl bg-[#0778aa] text-white"><Boxes className="size-6" /></span>
            <div><h3 className="text-2xl font-semibold">Precisa de algo específico?</h3><p className="mt-2 text-sm leading-relaxed text-white/50">Desenhamos o sistema, a integração ou a automação em torno do seu processo.</p></div>
            <button type="button" onClick={() => selectProduct('Projeto sob medida')} className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-bold text-[#344d56] transition hover:bg-[#d8eef7]">Falar sobre meu projeto<ArrowRight className="size-4 transition-transform group-hover:translate-x-1" /></button>
          </div>
        </div>
      </section>

      <section id="processo" className="mx-auto max-w-[1440px] px-6 py-28 lg:px-10 lg:py-36">
        <div className="grid gap-14 lg:grid-cols-[.85fr_1.15fr] lg:gap-24">
          <div>
            <SectionLabel>Da conversa à entrega</SectionLabel>
            <h2 className="max-w-xl text-4xl font-semibold leading-tight tracking-[-.05em] sm:text-5xl lg:text-6xl">Comprar tecnologia não precisa ser complicado.</h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/50">Você entende o que será entregue, como será implantado e quem estará ao seu lado em cada etapa.</p>
            <div className="mt-10 flex items-center gap-3 rounded-2xl border border-[#69c1e3]/25 bg-[#0778aa]/15 p-4 text-sm text-[#bce8f7]"><ShoppingCart className="size-5 shrink-0" />Contratação consultiva, sem empurrar produto.</div>
          </div>

          <div className="relative">
            <div className="absolute bottom-8 left-[1.45rem] top-8 w-px bg-gradient-to-b from-[#0778aa] via-[#69c1e3]/35 to-transparent" />
            {[
              ['01', 'Escolha o ponto de partida', 'Conte o desafio ou selecione um dos sistemas do catálogo.'],
              ['02', 'Veja funcionando', 'Receba uma demonstração focada no seu cenário e nas suas prioridades.'],
              ['03', 'Defina a implantação', 'Alinhamos dados, integrações, usuários, segurança, prazo e suporte.'],
              ['04', 'Comece com acompanhamento', 'Entramos em produção com treinamento, validação e evolução contínua.'],
            ].map(([number, title, text], index) => (
              <article key={number} className="relative grid grid-cols-[3rem_1fr] gap-5 py-6 first:pt-0">
                <span className={`relative z-10 grid size-12 place-items-center rounded-full border font-mono text-xs ${index === 0 ? 'border-[#0778aa] bg-[#0778aa] text-white' : 'border-white/12 bg-[#263f48] text-white/45'}`}>{number}</span>
                <div className="border-b border-white/[0.07] pb-7"><h3 className="text-xl font-semibold">{title}</h3><p className="mt-2 leading-relaxed text-white/45">{text}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="sobre" className="relative border-y border-white/[0.06] bg-[#263f48]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(7,120,170,.18),transparent_38%)]" />
        <div className="relative mx-auto grid max-w-[1440px] gap-14 px-6 py-28 lg:grid-cols-[1.1fr_.9fr] lg:px-10 lg:py-36">
          <div>
            <SectionLabel>A First Flow</SectionLabel>
            <p className="max-w-4xl text-4xl font-semibold leading-[1.12] tracking-[-.05em] sm:text-5xl lg:text-6xl">
              Conectamos ideias a resultados com tecnologia que parece simples — <span className="text-white/30">porque foi bem pensada.</span>
            </p>
          </div>
          <div className="flex flex-col justify-end lg:pl-12">
            <p className="text-lg leading-relaxed text-white/55">Acreditamos que transformação digital precisa ser acessível, confiável e contínua. Por isso, reunimos visão de negócio, design de produto, engenharia de software e operação em nuvem em uma única parceria.</p>
            <div className="mt-10 grid grid-cols-2 gap-3">
              {[
                [Layers3, 'Django + APIs'],
                [Zap, 'Vue + interfaces'],
                [Database, 'Dados conectados'],
                [ShieldCheck, 'Segurança aplicada'],
              ].map(([Icon, label]) => {
                const TechIcon = Icon as typeof Layers3;
                return <div key={label as string} className="flex items-center gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4 text-sm text-white/65"><TechIcon className="size-4 text-[#69c1e3]" />{label as string}</div>;
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 py-28 lg:px-10 lg:py-36">
        <div className="grid gap-12 rounded-[2.2rem] border border-white/[0.08] bg-gradient-to-br from-[#344d56] to-[#20343c] p-7 sm:p-10 lg:grid-cols-[1fr_1.2fr] lg:p-14">
          <div>
            <span className="grid size-14 place-items-center rounded-2xl bg-[#0778aa] text-white"><Cloud className="size-7" /></span>
            <h2 className="mt-8 text-4xl font-semibold tracking-[-.045em]">Preparado para a Azure.</h2>
            <p className="mt-4 max-w-md leading-relaxed text-white/50">Uma base moderna, segura e pronta para evoluir do site institucional aos sistemas comerciais da First Flow.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.035] p-6"><p className="text-xs font-bold uppercase tracking-[.16em] text-[#69c1e3]">O que validamos agora</p><ul className="mt-5 space-y-3 text-sm text-white/60">{['Mensagem e posicionamento', 'Catálogo e jornada comercial', 'Identidade e responsividade', 'Formulários e conversão'].map((item) => <li key={item} className="flex gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-[#69c1e3]" />{item}</li>)}</ul></div>
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.035] p-6"><p className="text-xs font-bold uppercase tracking-[.16em] text-[#9edcf2]">Arquitetura definida</p><p className="mt-5 text-sm leading-relaxed text-white/60">Frontend gerenciado na Azure, DNS migrado com segurança e futuros backends Django em contêineres — mantendo o site atual intacto até a troca final.</p></div>
          </div>
        </div>
      </section>

      <section id="faq" className="border-y border-white/[0.06] bg-white/[0.02]">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-6 py-28 lg:grid-cols-[.7fr_1.3fr] lg:px-10 lg:py-36">
          <div>
            <SectionLabel>Perguntas frequentes</SectionLabel>
            <h2 className="text-4xl font-semibold tracking-[-.045em] sm:text-5xl">Antes de começar.</h2>
            <p className="mt-5 max-w-sm leading-relaxed text-white/45">Respostas diretas para as dúvidas mais comuns sobre produto, implantação e nuvem.</p>
          </div>
          <Accordion className="border-t border-white/10">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`faq-${index}`} className="border-white/10">
                <AccordionTrigger className="py-6 text-base text-white hover:no-underline sm:text-lg">{faq.question}</AccordionTrigger>
                <AccordionContent className="max-w-2xl pb-6 text-base leading-relaxed text-white/48">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section id="contato" className="relative mx-auto max-w-[1440px] px-6 py-28 lg:px-10 lg:py-36">
        <div className="pointer-events-none absolute bottom-0 left-1/2 size-[46rem] -translate-x-1/2 rounded-full bg-[#0778aa]/18 blur-[130px]" />
        <div className="relative grid overflow-hidden rounded-[2.5rem] bg-[#0778aa] text-white lg:grid-cols-[1.15fr_.85fr]">
          <div className="p-8 sm:p-12 lg:p-16">
            <p className="text-xs font-bold uppercase tracking-[.18em] text-white/70">Próximo passo</p>
            <h2 className="mt-6 max-w-2xl text-5xl font-semibold leading-[.98] tracking-[-.06em] sm:text-6xl lg:text-7xl">Vamos colocar sua melhor ideia em fluxo?</h2>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/78">Escolha uma solução ou conte o desafio. A primeira conversa é direta, sem compromisso e focada no que pode gerar resultado.</p>
            <div className="mt-10 flex flex-wrap gap-5 text-sm font-semibold text-white/80"><span className="flex items-center gap-2"><MessageCircle className="size-4" /> Resposta humana</span><span className="flex items-center gap-2"><CheckCircle2 className="size-4" /> Diagnóstico inicial</span></div>
          </div>

          <form onSubmit={sendLead} className="m-3 rounded-[2rem] bg-[#344d56] p-6 text-white sm:m-5 sm:p-8 lg:p-10">
            <div className="flex items-center justify-between"><div><p className="text-sm font-semibold">Fale com a First Flow</p><p className="mt-1 text-xs text-white/40">Abriremos a conversa no WhatsApp</p></div><span className="grid size-10 place-items-center rounded-full bg-[#0778aa] text-white"><Send className="size-4" /></span></div>
            <div className="mt-8 space-y-5">
              <label className="block text-xs font-semibold text-white/55">Seu nome<input required value={name} onChange={(event) => setName(event.target.value)} placeholder="Como podemos chamar você?" className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 text-sm text-white outline-none transition placeholder:text-white/25 focus:border-[#69c1e3]/70 focus:ring-2 focus:ring-[#0778aa]/20" /></label>
              <label className="block text-xs font-semibold text-white/55">Empresa<input value={company} onChange={(event) => setCompany(event.target.value)} placeholder="Nome da empresa (opcional)" className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 text-sm text-white outline-none transition placeholder:text-white/25 focus:border-[#69c1e3]/70 focus:ring-2 focus:ring-[#0778aa]/20" /></label>
              <label className="block text-xs font-semibold text-white/55">Interesse<select value={interest} onChange={(event) => setInterest(event.target.value)} className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-[#263f48] px-4 text-sm text-white outline-none transition focus:border-[#69c1e3]/70 focus:ring-2 focus:ring-[#0778aa]/20"><option>Conhecer as soluções</option>{products.map((product) => <option key={product.title}>{product.title}</option>)}<option>Projeto sob medida</option><option>Site e presença digital</option><option>Migração para nuvem</option></select></label>
            </div>
            <button type="submit" className="group mt-7 flex h-13 w-full items-center justify-center gap-3 rounded-xl bg-[#0778aa] px-6 font-bold text-white transition hover:bg-[#0a8fc8]">Iniciar conversa<ArrowRight className="size-5 transition-transform group-hover:translate-x-1" /></button>
            <p className="mt-4 text-center text-[11px] leading-relaxed text-white/30">Seus dados são usados apenas para iniciar o atendimento solicitado.</p>
          </form>
        </div>
      </section>

      <footer className="border-t border-white/[0.07]">
        <div className="mx-auto max-w-[1440px] px-6 py-12 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_auto_auto] lg:gap-20">
            <div><img src="/firstflow-logo-light.png" alt="First Flow" className="h-12 w-auto" /><p className="mt-5 max-w-sm text-sm leading-relaxed text-white/38">Tecnologia customizada, sistemas prontos e soluções em nuvem para empresas que querem avançar com segurança.</p></div>
            <div><p className="text-xs font-bold uppercase tracking-[.15em] text-white/35">Contato</p><div className="mt-5 space-y-3 text-sm text-white/60"><a href="mailto:comercial@firstflow.com.br" className="flex items-center gap-2.5 transition hover:text-[#69c1e3]"><Mail className="size-4" />comercial@firstflow.com.br</a><a href={whatsappUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 transition hover:text-[#69c1e3]"><Phone className="size-4" />(21) 96985-8888</a><p className="flex max-w-xs items-start gap-2.5 leading-relaxed"><MapPin className="mt-0.5 size-4 shrink-0" />Av. das Américas, 4200, Barra da Tijuca — Rio de Janeiro, RJ</p></div></div>
            <div><p className="text-xs font-bold uppercase tracking-[.15em] text-white/35">Acompanhe</p><a href="https://www.linkedin.com/in/thiagomanhaestm/" target="_blank" rel="noreferrer" className="mt-5 grid size-11 place-items-center rounded-full border border-white/10 text-white/55 transition hover:border-[#69c1e3]/50 hover:bg-[#0778aa] hover:text-white" aria-label="LinkedIn"><ExternalLink className="size-5" /></a></div>
          </div>
          <div className="mt-12 flex flex-col gap-4 border-t border-white/[0.07] pt-7 text-xs text-white/30 sm:flex-row sm:items-center sm:justify-between"><p>© 2026 First Flow. Todos os direitos reservados.</p><div className="flex gap-5"><a href="https://www.firstflow.com.br/pol%C3%ADtica-de-privacidade" className="transition hover:text-white">Privacidade</a><a href="https://www.firstflow.com.br/c%C3%B3pia-pol%C3%ADtica-de-privacidade" className="transition hover:text-white">Termos de uso</a></div></div>
        </div>
      </footer>

      <a href={whatsappUrl} target="_blank" rel="noreferrer" aria-label="Falar com a First Flow no WhatsApp" className="fixed bottom-5 right-5 z-40 grid size-14 place-items-center rounded-full bg-[#0778aa] text-white shadow-[0_15px_45px_rgba(7,120,170,.32)] transition hover:scale-105 hover:bg-[#0a8fc8]"><MessageCircle className="size-6" /></a>
    </main>
  );
}
